import React from 'react';
import { useTrackPage } from '@olxeu-a2b/delivery-utils/src/tracking';
import { ReturnsCardContent } from '../returns-card/ReturnsCardContent';
import ReturnMethodDetails from './ReturnMethodDetails';
import ReturnPaymentDetails from './ReturnPaymentDetails';
import UserDetails from './UserDetails';
import SupportCenter from './SupportCenter';
import useI18n from '../../../../i18n/hooks/useI18n';
import useGetUserType from '../../hooks/useGetUserType';
import { PaymentInfo, ReturnItemDetails } from '../../../../../typings/returns';
import { USER_TYPE } from '../../constants';

import S from './style/ReturnsDetailsView';
import trackingKeys from '../../../../commons/constants/trackingKeys';

interface ReturnsDetailsViewProps {
  isDesktop: boolean;
  returnItemDetails: ReturnItemDetails;
  onFillDataClick: (returnId: string, userType: USER_TYPE) => void;
  refetch?: PaymentInfo;
}

const { returnDetails, returnOverview } = trackingKeys.touchPointPage.returns;

const ReturnsDetailsView: React.FC<ReturnsDetailsViewProps> = ({
  isDesktop,
  returnItemDetails,
  onFillDataClick,
  refetch,
}) => {
  const i18n = useI18n();
  const { isSeller } = useGetUserType({
    buyerId: returnItemDetails.buyerInfo.id,
    sellerId: returnItemDetails.sellerInfo.id,
  });

  const trackingExtras = {
    seller_type: isSeller ? USER_TYPE.SELLER : USER_TYPE.BUYER,
    ad_id: returnItemDetails.adInfo.id,
    seller_id: returnItemDetails.sellerInfo.id,
    touch_point_page: returnOverview,
  };

  useTrackPage(returnDetails, trackingExtras);

  const returnMethodSubtitle = isSeller
    ? i18n`dlv_returns_details_return_method_subtitle_seller`
    : i18n`dlv_returns_details_return_method_subtitle`;

  return (
    <S.Grid data-cy="returns-details-page">
      <S.GridElement>
        <ReturnsCardContent
          isDesktop={isDesktop}
          returnItem={returnItemDetails}
          onFillDataClick={onFillDataClick}
          showReturnsTransactionInfo={false}
          refetch={refetch}
        />
      </S.GridElement>
      <S.GridElement>
        <S.GridCardHeader>{i18n`dlv_returns_details_return_method`}</S.GridCardHeader>
        <S.GridCardSubtitle data-cy="returns-details-return-method-subtitle">
          {returnMethodSubtitle}
        </S.GridCardSubtitle>
        <S.GridCardContent>
          <ReturnMethodDetails isSeller={isSeller} deliveryDetails={returnItemDetails.delivery} />
        </S.GridCardContent>
      </S.GridElement>
      <S.GridElement>
        <S.GridCardHeader>{i18n`dlv_returns_details_payment_details`}</S.GridCardHeader>
        <S.GridCardSubtitle>{i18n`dlv_returns_details_payment_details_subtitle`}</S.GridCardSubtitle>
        <S.GridCardContent>
          <ReturnPaymentDetails
            isSeller={isSeller}
            orderInfo={returnItemDetails.order}
            deliveryDetails={returnItemDetails.delivery}
          />
        </S.GridCardContent>
      </S.GridElement>
      <S.GridElement>
        <S.GridCardHeader data-cy="returns-details-user-details-header">
          {isSeller ? i18n`dlv_returns_details_buyer` : i18n`dlv_returns_details_seller`}
        </S.GridCardHeader>
        <S.GridCardContent>
          <UserDetails
            userData={isSeller ? returnItemDetails.buyerInfo : returnItemDetails.sellerInfo}
          />
        </S.GridCardContent>
      </S.GridElement>
      <S.GridElement>
        <S.GridCardHeader>{i18n`dlv_returns_details_support_title`}</S.GridCardHeader>
        <S.GridCardContent>
          <SupportCenter isSeller={isSeller} trackingExtras={trackingExtras} />
        </S.GridCardContent>
      </S.GridElement>
    </S.Grid>
  );
};

export default ReturnsDetailsView;
